import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { synthwave84 } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import { createIconElement } from '@/lib';
import icon1 from '@/assets/icons/ic-Carousel.svg';
import icon2 from '@/assets/icons/ic-Customize.svg';
import icon3 from '@/assets/icons/ic-Divider.svg';

export default function InternalsTabsPage() {
    const [activeExample, setActiveExample] = useState('preview');
    const [activeBasic, setActiveBasic] = useState('preview');
    const [activeWithIcons, setActiveWithIcons] = useState('preview');
    const [activeDisabled, setActiveDisabled] = useState('preview');

    // Example tab states
    const [exampleTab, setExampleTab] = useState('tab-1');
    const [basicTab, setBasicTab] = useState('overview');
    const [iconTab, setIconTab] = useState('design');
    const [disabledTab, setDisabledTab] = useState('active-1');

    const CodeBlock = ({ code }: { code: string }) => (
        <div className='border-muted overflow-hidden rounded-md border'>
            <div className='text-body-md [&_code]:w-full! [&_code]:!p-0 [&_code]:!text-base [&_code]:!leading-relaxed [&_pre]:!m-0 [&_pre]:!p-6'>
                <SyntaxHighlighter language='typescript' style={synthwave84} showLineNumbers={false}>
                    {code.trim()}
                </SyntaxHighlighter>
            </div>
        </div>
    );

    return (
        <div className='mx-auto max-w-6xl space-y-12 p-6 pb-40'>
            {/* Page Header */}
            <div className='space-y-2'>
                <h1 className='text-heading-h1 text-dark'>StanTabs</h1>
                <p className='text-body-lg text-gray'>A flexible tabs component built on top of Radix UI.</p>
            </div>

            {/* Basic Example */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Basic Usage</h2>
                    <p className='text-body-md text-gray mt-2'>Simple tabs with text labels.</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveBasic('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveBasic('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeBasic === 'preview' ? (
                            <StanTabs value={basicTab} onValueChange={setBasicTab}>
                                <StanTabList className='mb-4'>
                                    <StanTab value='overview'>Overview</StanTab>
                                    <StanTab value='analytics'>Analytics</StanTab>
                                    <StanTab value='settings'>Settings</StanTab>
                                </StanTabList>

                                <StanTabContent value='overview'>
                                    <div className='bg-info-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-info mb-2'>Overview</h3>
                                        <p className='text-label-sm-regular text-info'>
                                            This is the overview tab content. You can put any React components here.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='analytics'>
                                    <div className='bg-success-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-success mb-2'>Analytics</h3>
                                        <p className='text-label-sm-regular text-success'>
                                            View your analytics and metrics here.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='settings'>
                                    <div className='bg-brand-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-brand-main mb-2'>Settings</h3>
                                        <p className='text-label-sm-regular text-brand-main'>
                                            Configure your preferences here.
                                        </p>
                                    </div>
                                </StanTabContent>
                            </StanTabs>
                        ) : (
                            <CodeBlock
                                code={`import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';

export const BasicExample = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <StanTabs value={activeTab} onValueChange={setActiveTab}>
      <StanTabList>
        <StanTab value='overview'>Overview</StanTab>
        <StanTab value='analytics'>Analytics</StanTab>
        <StanTab value='settings'>Settings</StanTab>
      </StanTabList>

      <StanTabContent value='overview'>
        <div>Overview content</div>
      </StanTabContent>

      <StanTabContent value='analytics'>
        <div>Analytics content</div>
      </StanTabContent>

      <StanTabContent value='settings'>
        <div>Settings content</div>
      </StanTabContent>
    </StanTabs>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* With Icons */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>With Icons</h2>
                    <p className='text-body-md text-gray mt-2'>Tabs can include icons alongside labels.</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveWithIcons('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeWithIcons === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveWithIcons('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeWithIcons === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeWithIcons === 'preview' ? (
                            <StanTabs value={iconTab} onValueChange={setIconTab}>
                                <StanTabList className='mb-4'>
                                    <StanTab value='design'>
                                        <span className='flex items-center gap-2'>
                                            {createIconElement(icon1, { className: 'size-5' })}
                                            <span>Design</span>
                                        </span>
                                    </StanTab>
                                    <StanTab value='develop'>
                                        <span className='flex items-center gap-2'>
                                            {createIconElement(icon2, { className: 'size-5' })}
                                            <span>Develop</span>
                                        </span>
                                    </StanTab>
                                    <StanTab value='deploy'>
                                        <span className='flex items-center gap-2'>
                                            {createIconElement(icon3, { className: 'size-5' })}
                                            <span>Deploy</span>
                                        </span>
                                    </StanTab>
                                </StanTabList>

                                <StanTabContent value='design'>
                                    <div className='bg-critical-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-critical mb-2'>Design Phase</h3>
                                        <p className='text-label-sm-regular text-critical'>
                                            Create beautiful user interfaces.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='develop'>
                                    <div className='bg-seafoam-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-seafoam mb-2'>Development Phase</h3>
                                        <p className='text-label-sm-regular text-seafoam'>Build robust applications.</p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='deploy'>
                                    <div className='bg-notice-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-notice mb-2'>Deployment Phase</h3>
                                        <p className='text-label-sm-regular text-notice'>
                                            Ship your product to production.
                                        </p>
                                    </div>
                                </StanTabContent>
                            </StanTabs>
                        ) : (
                            <CodeBlock
                                code={`import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import { createIconElement } from '@/lib';
import DesignIcon from '@/assets/icons/ic-design.svg';

export const WithIconsExample = () => {
  const [activeTab, setActiveTab] = useState('design');

  return (
    <StanTabs value={activeTab} onValueChange={setActiveTab}>
      <StanTabList>
        <StanTab value='design'>
          <span className='flex items-center gap-2'>
            {createIconElement(DesignIcon, {className: 'size-5'})}
            <span>Design</span>
          </span>
        </StanTab>
        <StanTab value='develop'>
          <span className='flex items-center gap-2'>
            {createIconElement(DevelopIcon, {className: 'size-5'})}
            <span>Develop</span>
          </span>
        </StanTab>
        <StanTab value='deploy'>
          <span className='flex items-center gap-2'>
            {createIconElement(DeployIcon, {className: 'size-5'})}
            <span>Deploy</span>
          </span>
        </StanTab>
      </StanTabList>

      <StanTabContent value='design'>
        <div>Design content</div>
      </StanTabContent>
      {/* ... other tab contents */}
    </StanTabs>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Disabled Tabs */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Disabled State</h2>
                    <p className='text-body-md text-gray mt-2'>Tabs can be disabled to prevent interaction.</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveDisabled('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeDisabled === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveDisabled('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeDisabled === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeDisabled === 'preview' ? (
                            <StanTabs value={disabledTab} onValueChange={setDisabledTab}>
                                <StanTabList className='mb-4'>
                                    <StanTab value='active-1'>Active Tab 1</StanTab>
                                    <StanTab value='active-2'>Active Tab 2</StanTab>
                                    <StanTab value='disabled' disabled>
                                        Disabled Tab
                                    </StanTab>
                                </StanTabList>

                                <StanTabContent value='active-1'>
                                    <div className='bg-success-subtle rounded-md p-6'>
                                        <p className='text-label-sm-regular text-success'>
                                            This tab is active and clickable.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='active-2'>
                                    <div className='bg-warning-subtle rounded-md p-6'>
                                        <p className='text-label-sm-regular text-warning'>
                                            This tab is also active and clickable.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='disabled'>
                                    <div className='bg-neutral-subtle rounded-md p-6'>
                                        <p className='text-label-sm-regular text-disabled'>
                                            This content is not accessible.
                                        </p>
                                    </div>
                                </StanTabContent>
                            </StanTabs>
                        ) : (
                            <CodeBlock
                                code={`import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';

export const DisabledExample = () => {
  const [activeTab, setActiveTab] = useState('active-1');

  return (
    <StanTabs value={activeTab} onValueChange={setActiveTab}>
      <StanTabList>
        <StanTab value='active-1'>Active Tab 1</StanTab>
        <StanTab value='active-2'>Active Tab 2</StanTab>
        <StanTab value='disabled' disabled>
          Disabled Tab
        </StanTab>
      </StanTabList>

      <StanTabContent value='active-1'>
        <div>Active content 1</div>
      </StanTabContent>

      <StanTabContent value='active-2'>
        <div>Active content 2</div>
      </StanTabContent>

      <StanTabContent value='disabled'>
        <div>Disabled content</div>
      </StanTabContent>
    </StanTabs>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Complete Example */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Complete Example</h2>
                    <p className='text-body-md text-gray mt-2'>
                        A full-featured example with multiple tabs and rich content.
                    </p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveExample('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeExample === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveExample('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeExample === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeExample === 'preview' ? (
                            <StanTabs value={exampleTab} onValueChange={setExampleTab}>
                                <StanTabList className='mb-4'>
                                    <StanTab value='tab-1'>Dashboard</StanTab>
                                    <StanTab value='tab-2'>Users</StanTab>
                                    <StanTab value='tab-3'>Reports</StanTab>
                                    <StanTab value='tab-4' disabled>
                                        Settings
                                    </StanTab>
                                </StanTabList>

                                <StanTabContent value='tab-1'>
                                    <div className='bg-info-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-info mb-3'>Dashboard Overview</h3>
                                        <div className='grid grid-cols-3 gap-4'>
                                            <div className='bg-canvas rounded p-4 shadow-sm'>
                                                <p className='text-label-xs-regular text-gray'>Total Revenue</p>
                                                <p className='text-heading-h2 text-info'>$45,231</p>
                                            </div>
                                            <div className='bg-canvas rounded p-4 shadow-sm'>
                                                <p className='text-label-xs-regular text-gray'>Active Users</p>
                                                <p className='text-heading-h2 text-success'>2,345</p>
                                            </div>
                                            <div className='bg-canvas rounded p-4 shadow-sm'>
                                                <p className='text-label-xs-regular text-gray'>Conversion Rate</p>
                                                <p className='text-heading-h2 text-brand-main'>12.5%</p>
                                            </div>
                                        </div>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='tab-2'>
                                    <div className='bg-success-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-success mb-3'>User Management</h3>
                                        <p className='text-label-sm-regular text-success'>
                                            Manage your users and permissions here.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='tab-3'>
                                    <div className='bg-brand-subtle rounded-md p-6'>
                                        <h3 className='text-label-md-medium text-brand-main mb-3'>
                                            Reports & Analytics
                                        </h3>
                                        <p className='text-label-sm-regular text-brand-main'>
                                            View detailed reports and analytics data.
                                        </p>
                                    </div>
                                </StanTabContent>

                                <StanTabContent value='tab-4'>
                                    <div className='bg-neutral-subtle rounded-md p-6'>
                                        <p className='text-label-sm-regular text-disabled'>
                                            Settings are currently unavailable.
                                        </p>
                                    </div>
                                </StanTabContent>
                            </StanTabs>
                        ) : (
                            <CodeBlock
                                code={`import { useState } from 'react';
import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';

export const CompleteExample = () => {
  const [activeTab, setActiveTab] = useState('tab-1');

  return (
    <StanTabs value={activeTab} onValueChange={setActiveTab}>
      <StanTabList>
        <StanTab value='tab-1'>Dashboard</StanTab>
        <StanTab value='tab-2'>Users</StanTab>
        <StanTab value='tab-3'>Reports</StanTab>
        <StanTab value='tab-4' disabled>Settings</StanTab>
      </StanTabList>

      <StanTabContent value='tab-1'>
        <div className='rounded-md bg-info-subtle p-6'>
          <h3 className='mb-3 font-semibold'>Dashboard Overview</h3>
          {/* Dashboard content */}
        </div>
      </StanTabContent>

      <StanTabContent value='tab-2'>
        <div className='rounded-md bg-success-subtle p-6'>
          <h3 className='mb-3 font-semibold'>User Management</h3>
          {/* User management content */}
        </div>
      </StanTabContent>

      <StanTabContent value='tab-3'>
        <div className='rounded-md bg-brand-subtle p-6'>
          <h3 className='mb-3 font-semibold'>Reports & Analytics</h3>
          {/* Reports content */}
        </div>
      </StanTabContent>

      <StanTabContent value='tab-4'>
        <div className='rounded-md bg-neutral-subtle p-6'>
          <p className='text-sm text-disabled'>Settings unavailable</p>
        </div>
      </StanTabContent>
    </StanTabs>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* API Reference */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>API Reference</h2>
                </div>

                <div className='space-y-4'>
                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>StanTabs</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    value
                                </span>
                                <span className='text-body-md text-gray pt-1'>
                                    The controlled value of the active tab
                                </span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    onValueChange
                                </span>
                                <span className='text-body-md text-gray pt-1'>Callback when tab changes</span>
                            </div>
                        </div>
                    </div>

                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>StanTab</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    value
                                </span>
                                <span className='text-body-md text-gray pt-1'>Unique identifier for this tab</span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    disabled
                                </span>
                                <span className='text-body-md text-gray pt-1'>
                                    Whether the tab is disabled (optional)
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>StanTabContent</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    value
                                </span>
                                <span className='text-body-md text-gray pt-1'>Must match a StanTab value</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
